import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileIcon } from '@phosphor-icons/react';
import { finnKoblingBlokkering, usePlandokumenterForPlan } from '@origo/planregister';
import type { Plandokument } from '@origo/planregister';
import { Button, Checkbox, Dialog, Loader, Notice, SearchInput } from '@origo/origo-ui';
import { EmptyState } from '@geodata/georg';
import { sortNewestFirst, toDocumentItem } from '../../../mappers/toDocuments';
import { FILE_ICONS } from './fileIcons';
import styles from './LinkExistingDocumentsDialog.module.css';

interface LinkExistingDocumentsDialogProps {
    open: boolean;
    napId: string;
    onClose: () => void;
    onConfirm: (dokumenter: Plandokument[]) => void;
    isSaving?: boolean;
}

/** Velg plandokumenter som ikke er knyttet til noen behandling, for å knytte dem
 *  til gjeldende behandling. Dokumenter knyttet til en dispensasjon
 *  kan også velges. */
const LinkExistingDocumentsDialog: React.FC<LinkExistingDocumentsDialogProps> = ({
    open,
    napId,
    onClose,
    onConfirm,
    isSaving = false,
}) => {
    const { t } = useTranslation();
    const baseId = useId();
    const { data, isLoading, isFetching, isError, refetch } = usePlandokumenterForPlan(open ? napId : undefined);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<Set<string>>(() => new Set());

    // Ny økt hver gang dialogen åpnes.
    useEffect(() => {
        if (open) {
            setSearch('');
            setSelected(new Set());
        }
    }, [open]);

    // Nyeste øverst, samme sortering som Plandokumenter-fanen.
    const candidates = useMemo(
        () =>
            sortNewestFirst((data ?? []).filter(doc => !finnKoblingBlokkering(doc, napId))).map(doc => ({
                doc,
                item: toDocumentItem(doc, t),
            })),
        [data, napId, t]
    );

    const visible = useMemo(() => {
        const query = search.trim().toLocaleLowerCase('nb');
        if (!query) return candidates;
        return candidates.filter(({ item }) => item.dokumentnavn.toLocaleLowerCase('nb').includes(query));
    }, [candidates, search]);

    const toggle = (id: string) =>
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    // Kun valgte som fortsatt er kandidater — en henting kan ha fjernet noen
    // (knyttet av andre i mellomtiden), og da skal knappen ikke telle dem.
    const chosen = useMemo(() => candidates.filter(({ item }) => selected.has(item.id)), [candidates, selected]);
    const confirm = () => onConfirm(chosen.map(({ doc }) => doc));

    const count = chosen.length;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('processings.documents.linkExisting.title')}
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isSaving}>
                        {t('common.cancel')}
                    </Button>
                    {/* Hold igjen mens listen hentes på nytt, så et utdatert valg ikke sendes. */}
                    <Button variant="primary" onClick={confirm} disabled={count === 0 || isSaving || isFetching}>
                        {count > 0
                            ? t('processings.documents.linkExisting.submitCount', { count })
                            : t('processings.documents.linkExisting.submit')}
                    </Button>
                </>
            }
        >
            <div className={styles.body}>
                <p className={styles.description}>{t('processings.documents.linkExisting.description')}</p>
                {isLoading ? (
                    <Loader label={t('common.loading')} />
                ) : isError && !data ? (
                    <>
                        <Notice variant="danger">{t('common.errorLoading')}</Notice>
                        <div>
                            <Button variant="secondary" size="md" onClick={() => refetch()}>
                                {t('common.retry')}
                            </Button>
                        </div>
                    </>
                ) : candidates.length === 0 ? (
                    <EmptyState
                        icon={<FileIcon size={32} weight="duotone" />}
                        title={t('processings.documents.linkExisting.empty.title')}
                        description={t('processings.documents.linkExisting.empty.description')}
                    />
                ) : (
                    <>
                        <SearchInput
                            label={t('processings.documents.linkExisting.search')}
                            placeholder={t('processings.documents.linkExisting.search')}
                            hideLabel
                            size="md"
                            value={search}
                            onChange={setSearch}
                        />
                        {visible.length === 0 ? (
                            <p className={styles.noMatches}>{t('processings.documents.linkExisting.noMatches')}</p>
                        ) : (
                            <ul className={styles.list}>
                                {visible.map(({ item }) => {
                                    const Icon = FILE_ICONS[item.filtype];
                                    const meta = [item.dokumenttype, item.dato].filter(Boolean).join(' · ');
                                    const metaId = `${baseId}-${item.id}-meta`;
                                    const isSelected = selected.has(item.id);
                                    return (
                                        <li
                                            key={item.id}
                                            className={`${styles.row} ${isSelected ? styles.selected : ''}`}
                                        >
                                            <Icon className={styles.icon} size={20} weight="duotone" aria-hidden="true" />
                                            <div className={styles.text}>
                                                <Checkbox
                                                    label={item.dokumentnavn}
                                                    checked={isSelected}
                                                    onChange={() => toggle(item.id)}
                                                    aria-describedby={meta ? metaId : undefined}
                                                />
                                                {meta && (
                                                    <span id={metaId} className={styles.meta}>
                                                        {meta}
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </>
                )}
            </div>
        </Dialog>
    );
};

export default LinkExistingDocumentsDialog;
